// src/components/prestige/presAlert.js
import React, { useState, useEffect, useRef } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  ChevronDown,
  Eye,
  Clock,
  ImageOff,
  Moon,
  BarChart3,
  PieChart,
  MapPin,
  Camera,
  AlertCircle,
} from "lucide-react";
import { Modal, Select, Pagination } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import axios from '../../utils/axiosDash';
import { eventEmitter } from '../../utils/eventEmitter';

const { Option } = Select;

// Function to check if time is between 9:30 PM and 5:00 AM
function isLateNight(timeStr) {
  if (!timeStr) return false;
  
  const parts = timeStr.split(':');
  if (parts.length < 2) return false;
  
  const hours = parseInt(parts[0]);
  const minutes = parseInt(parts[1]);
  
  const totalMinutes = hours * 60 + minutes;
  
  const LATE_NIGHT_START = 21 * 60 + 30;
  const LATE_NIGHT_END = 5 * 60;
  
  return totalMinutes >= LATE_NIGHT_START || totalMinutes <= LATE_NIGHT_END;
}

function deriveLevelFromType(type, item) {
  if (type === 'intrusion' || type === 'loitering') {
    return 'critical';
  }
  return 'ok';
}

function isoToTime(isoStr) {
  if (!isoStr) return "—";
  const str = typeof isoStr === "object" ? isoStr?.toISOString?.() ?? "" : String(isoStr);
  const t = str.split("T")[1];
  return t ? t.substring(0, 8) : "—";
}

function isoToDate(isoStr) {
  if (!isoStr) return "—";
  const str = typeof isoStr === "object" ? isoStr?.toISOString?.() ?? "" : String(isoStr);
  const d = str.split("T")[0];
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

function getEventTypeLabel(type) {
  const mapping = {
    'ppe': 'PPE',
    'intrusion': 'Intrusion',
    'overcrowding': 'Crowd density',
    'tailgating': 'Tailgating',
    'head_count': 'Head Count',
    'loitering': 'Loitering',
    'fall': 'Fall detected',
    'restricted_entry': 'Restricted entry',
    'altercation': 'Altercation',
    'unattended_object': 'Unattended object',
    'suspicious': 'Suspicious behaviour',
  };
  return mapping[type] || type || 'Unknown';
}

const levelCfg = {
  critical: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-500",
    label: "Critical",
    Icon: AlertOctagon,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-500",
    label: "Warning",
    Icon: AlertTriangle,
  },
  ok: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-500",
    label: "OK",
    Icon: CheckCircle2,
  },
};

const Label = ({ children, className = "" }) => (
  <span className={`text-[10px] font-bold uppercase tracking-[0.8px] text-slate-500 ${className}`}>
    {children}
  </span>
);

const Alerts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const alertRefs = useRef({});
  const [highlightProcessed, setHighlightProcessed] = useState(false);
  const [pendingHighlightId, setPendingHighlightId] = useState(null);
  const [autoOpenSnapshot, setAutoOpenSnapshot] = useState(false);
  
  const [camFilter,    setCamFilter]    = useState("All");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [dateFilter,   setDateFilter]   = useState("");
  const [expanded,     setExpanded]     = useState(null);
  const [previewAlert, setPreviewAlert] = useState(null);
  const [alerts,       setAlerts]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [availableZoneTypes, setAvailableZoneTypes] = useState([]);
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    byType: [],
    outcomeBreakdown: [],
    repeatLocations: [],
    totalAlerts: 0,
    insights: {
      topLocations: '',
      topLocationsPercentage: 0,
      criticalPercentage: 0,
      criticalCount: 0,
      highPersonCount: 0,
    }
  });

  // ✅ Listen for new alerts
  useEffect(() => {
    const handleNewAlert = (alertData) => {
      const alertId = alertData._id || alertData.id;
      
      setPendingHighlightId(alertId);
      setAutoOpenSnapshot(false);
      
      const exists = alerts.some(a => a.id === alertId);
      if (!exists) {
        fetchPrestigeAlerts(currentPage, pageSize, true);
        fetchAnalytics(); // Also refresh analytics
      } else {
        highlightAlert(alertId, false);
      }
    };

    const unsubscribe = eventEmitter.on('newAlert', handleNewAlert);
    return () => {
      unsubscribe();
    };
  }, [alerts, currentPage, pageSize]);

  // ✅ Listen for highlightAlert events
  useEffect(() => {
    const handleHighlightAlert = ({ alertId, alertData, openSnapshot }) => {
      
      setPendingHighlightId(alertId);
      setAutoOpenSnapshot(openSnapshot || false);
      
      const exists = alerts.some(a => a.id === alertId);
      if (exists) {
        highlightAlert(alertId, openSnapshot || false);
      } else {
        fetchPrestigeAlerts(currentPage, pageSize, true);
      }
    };

    const unsubscribe = eventEmitter.on('highlightAlert', handleHighlightAlert);
    return () => {
      unsubscribe();
    };
  }, [alerts, currentPage, pageSize]);

  // ✅ Handle highlighted alert from navigation
  useEffect(() => {
    if (location.state?.highlightAlert && !highlightProcessed) {
      const alertId = location.state.highlightAlert;
      const openSnapshot = location.state?.openSnapshot || false;
      
      setPendingHighlightId(alertId);
      setAutoOpenSnapshot(openSnapshot);
      
      const exists = alerts.some(a => a.id === alertId);
      if (exists) {
        highlightAlert(alertId, openSnapshot);
        setHighlightProcessed(true);
        navigate(location.pathname, { replace: true, state: {} });
      } else {
        fetchPrestigeAlerts(currentPage, pageSize, true);
      }
    }
  }, [location.state, navigate, highlightProcessed, alerts, currentPage, pageSize]);

  // ✅ Function to highlight an alert
  const highlightAlert = (alertId, openSnapshot = false) => {

    setExpanded(alertId);
    
    const scrollAndHighlight = () => {
      const element = alertRefs.current[alertId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        element.style.transition = 'all 0.5s ease';
        element.style.backgroundColor = '#fef3c7';
        element.style.borderColor = '#f59e0b';
        element.style.borderWidth = '2px';
        element.style.boxShadow = '0 0 20px rgba(245, 158, 11, 0.3)';
        
        if (openSnapshot) {
          const alertData = alerts.find(a => a.id === alertId);
          if (alertData && alertData.hasImages) {
            setTimeout(() => {
              setPreviewAlert({
                imgUrls: alertData.imgUrls,
                cam: alertData.cam,
                location: alertData.location,
                module: alertData.module,
                currentIndex: 0,
              });
            }, 800);
          }
        }
        
        setTimeout(() => {
          element.style.backgroundColor = '';
          element.style.borderColor = '';
          element.style.borderWidth = '';
          element.style.boxShadow = '';
        }, 4000);
        
        setHighlightProcessed(true);
        setPendingHighlightId(null);
        setAutoOpenSnapshot(false);
      } else {
        setTimeout(scrollAndHighlight, 500);
      }
    };
    
    setTimeout(scrollAndHighlight, 300);
  };

  // ✅ NEW: Fetch analytics separately
  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      
      const response = await axios.post("/fetch-prestige-alert-analytics", {
        camera: camFilter,
        zoneType: moduleFilter,
        days: 30 // Last 30 days
      });

      if (response.data.success) {
        setAnalytics({
          byType: response.data.data.byType || [],
          outcomeBreakdown: response.data.data.outcomeBreakdown || [],
          repeatLocations: response.data.data.repeatLocations || [],
          totalAlerts: response.data.data.totalAlerts || 0,
          insights: response.data.data.insights || {
            topLocations: '',
            topLocationsPercentage: 0,
            criticalPercentage: 0,
            criticalCount: 0,
            highPersonCount: 0,
          }
        });
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // ✅ fetchPrestigeAlerts with optional keepHighlight
  const fetchPrestigeAlerts = async (page = currentPage, limit = pageSize, keepHighlight = false) => {
    try {
      setLoading(true);

      const response = await axios.post("/fetch-prestige-alert", {
        page,
        limit,
        camera: camFilter,
        zoneType: moduleFilter,
        date: dateFilter
      });

      if (response.data.success) {
        const formattedAlerts = response.data.alerts.map((item, index) => {
          const imageUrl = item.image_path || item.image_url || null;

          const moduleDisplayName = {
            'ppe': 'PPE',
            'intrusion': 'Intrusion',
            'overcrowding': 'Overcrowding',
            'tailgating': 'Tailgating',
            'head_count': 'Head Count',
            'loitering': 'Loitering',
          }[item.type] || item.type || 'Unknown';

          const timeStr = isoToTime(item.timestamp);
          const isLate = isLateNight(timeStr);

          return {
            id: item._id || `alert_${index}`,
            time: timeStr,
            date: isoToDate(item.timestamp),
            timestamp: item.timestamp,
            cam: item.camera_id,
            camName: item.camera_id,
            location: item.camera_id || 'Unknown',
            module: moduleDisplayName,
            originalType: item.type,
            type: item.type,
            level: deriveLevelFromType(item.type, item),
            ppeClasses: item.ppe_classes || [],
            personCount: item.person_count || 0,
            imgUrls: imageUrl
              ? [{
                  url: imageUrl,
                  time: item.timestamp,
                }]
              : [],
            hasImages: !!imageUrl,
            isLateNight: isLate,
          };
        });

        const sorted = formattedAlerts.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        setAlerts(sorted);
        setTotalAlerts(response.data.pagination.totalRecords);
        setCurrentPage(response.data.pagination.page);
        setPageSize(response.data.pagination.limit);
        
        if (response.data.filters) {
          setAvailableCameras(response.data.filters.cameras || []);
          setAvailableZoneTypes(response.data.filters.zoneTypes || []);
        }

        const pendingId = pendingHighlightId || location.state?.highlightAlert;
        if (pendingId && !highlightProcessed) {
          const alertExists = sorted.some(a => a.id === pendingId);
          if (alertExists) {
            highlightAlert(pendingId, autoOpenSnapshot);
            if (location.state?.highlightAlert) {
              navigate(location.pathname, { replace: true, state: {} });
            }
          } else {
            if (page !== 1 && !keepHighlight) {
              setPendingHighlightId(pendingId);
              if (currentPage !== 1) {
                setCurrentPage(1);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching Prestige Alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Effect to handle page change for finding alerts
  useEffect(() => {
    if (pendingHighlightId && currentPage === 1) {
      fetchPrestigeAlerts(1, pageSize, true);
    }
  }, [currentPage, pendingHighlightId]);

  // Initial load and filter changes
  useEffect(() => {
    fetchPrestigeAlerts(currentPage, pageSize);
    fetchAnalytics(); // ✅ Fetch analytics whenever filters change
  }, [currentPage, pageSize, camFilter, moduleFilter, dateFilter]);

  // Image preview effect
  useEffect(() => {
    if (!previewAlert) {
      setImageLoading(false);
      return;
    }
    const current = previewAlert.imgUrls?.[previewAlert.currentIndex];
    if (!current?.url) {
      setImageLoading(false);
      return;
    }
    setImageLoading(true);
    const img = new Image();
    img.onload  = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
    img.src = current.url;
  }, [previewAlert]);

  const hasActiveFilter =
    camFilter !== "All" || moduleFilter !== "All" || dateFilter !== "";

  const totalAlertCount = analytics.totalAlerts || totalAlerts;

  return (
    <div className="flex flex-col gap-6 p-6 bg-slate-50 min-h-screen">
      {/* ── Header ── */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Label className="text-red-500">Live + Historical</Label>
          <h1 className="mt-1 text-[26px] font-bold tracking-[-0.5px] text-slate-900">
            Alert Centre
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Security events across monitored zones
          </p>
        </div>
        <button
          onClick={() => {
            fetchPrestigeAlerts(currentPage, pageSize);
            fetchAnalytics();
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Camera */}
          <div className="flex flex-col gap-1">
            <Label>Camera</Label>
            <Select 
              value={camFilter} 
              onChange={(value) => {
                setCamFilter(value);
                setCurrentPage(1);
              }} 
              className="w-[180px]"
              size="middle"
            >
              <Option value="All">All Cameras</Option>
              {availableCameras.map((c) => (
                <Option key={c} value={c}>{c}</Option>
              ))}
            </Select>
          </div>

          {/* Zone Type */}
          <div className="flex flex-col gap-1">
            <Label>Zone Type</Label>
            <Select 
              value={moduleFilter} 
              onChange={(value) => {
                setModuleFilter(value);
                setCurrentPage(1);
              }} 
              className="w-[180px]"
              size="middle"
            >
              <Option value="All">All Zone Types</Option>
              {availableZoneTypes.map((m) => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Select>
          </div>

          {/* Date Filter */}
          <div className="flex flex-col gap-1">
            <Label>Date</Label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>

          {hasActiveFilter && (
            <button
              onClick={() => {
                setCamFilter("All");
                setModuleFilter("All");
                setDateFilter("");
                setCurrentPage(1);
              }}
              className="mb-0.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-[7px] text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            >
              Clear filters
            </button>
          )}

          <div className="ml-auto mb-0.5 rounded-lg bg-slate-100 px-3 py-[7px] text-xs font-bold text-slate-600">
            {totalAlertCount} total alerts
          </div>
        </div>
      </div>

      {/* ── Alerts List ── */}
      <div className="flex flex-col gap-3">
        {loading && (
          <div className="py-10 text-center text-sm text-slate-500 font-medium">
            Loading alerts…
          </div>
        )}

        {!loading && alerts.map((a) => {
          const cfg  = levelCfg[a.level];
          const isOpen = expanded === a.id;

          return (
            <div
              key={a.id}
              ref={el => alertRefs.current[a.id] = el}
              id={`alert-${a.id}`}
              className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-all ${cfg.border}`}
            >
              {/* ── Main Row ── */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors flex-wrap"
                onClick={() => setExpanded(isOpen ? null : a.id)}
              >
                {/* Severity icon */}
                <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${cfg.bg}`}>
                  <cfg.Icon size={15} className={cfg.text} />
                </div>

                {/* Time */}
                <div className="w-20 flex-shrink-0">
                  <p className="font-mono text-xs font-bold text-slate-700">{a.time}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">{a.date}</p>
                </div>

                {/* Camera Name with Late Night Tag */}
                <div className="flex flex-shrink-0 items-center gap-2">
                  <div className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1">
                    <ShieldAlert size={12} className="text-slate-500" />
                    <span className="text-[11px] font-bold text-slate-600">{a.cam}</span>
                  </div>
                  
                  {a.isLateNight && (
                    <div className="flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 border border-indigo-200">
                      <Moon size={10} className="text-indigo-600" />
                      <span className="text-[9px] font-bold text-indigo-700 whitespace-nowrap">
                        Late Night
                      </span>
                    </div>
                  )}
                </div>

                {/* Zone Type */}
                <span className={`flex-shrink-0 rounded-md px-2 py-1 text-[10px] font-bold ${
                  a.originalType === 'intrusion' ? 'bg-red-50 text-red-600' :
                  a.originalType === 'loitering' ? 'bg-amber-50 text-amber-600' :
                  a.originalType === 'overcrowding' ? 'bg-orange-50 text-orange-600' :
                  a.originalType === 'tailgating' ? 'bg-amber-50 text-amber-600' :
                  a.originalType === 'ppe' ? 'bg-blue-50 text-blue-600' :
                  a.originalType === 'head_count' ? 'bg-purple-50 text-purple-600' :
                  'bg-gray-50 text-gray-600'
                }`}>
                  {a.module}
                </span>

                {/* PPE Classes */}
                {a.module === "PPE" && a.ppeClasses?.length > 0 && (
                  <div className="flex flex-shrink-0 items-center gap-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1">
                    <ShieldAlert size={11} className="text-amber-500" />
                    <span className="text-[11px] font-bold text-amber-600">
                      {a.ppeClasses.join(", ")}
                    </span>
                  </div>
                )}

                {/* Location */}
                <span className="flex-1 truncate text-sm text-slate-700 min-w-[100px]">
                  {a.location}
                </span>

                {/* View Snapshot / No Image */}
                {a.hasImages ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewAlert({
                        imgUrls: a.imgUrls,
                        cam: a.cam,
                        location: a.location,
                        module: a.module,
                        currentIndex: 0,
                      });
                    }}
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11px] font-bold text-violet-600 transition hover:bg-violet-100"
                  >
                    <Eye size={13} />
                    View Snapshot
                  </button>
                ) : (
                  <div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-400">
                    <ImageOff size={13} />
                    No Image
                  </div>
                )}

                {/* Severity pill */}
                <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${cfg.bg} ${cfg.text}`}>
                  {cfg.label}
                </span>

                <ChevronDown
                  size={15}
                  className={`flex-shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
                />
              </div>

              {/* ── Expanded Section ── */}
              {isOpen && (
                <div className="flex flex-col gap-5 border-t border-slate-200 bg-slate-50 px-4 py-4">
                  {/* Meta Information */}
                  <div className="flex flex-wrap gap-6">
                    <div>
                      <Label>Camera</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.cam}</p>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.location}</p>
                    </div>
                    <div>
                      <Label>Zone Type</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.module}</p>
                    </div>
                    <div>
                      <Label>Event Type</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.eventType || a.module}</p>
                    </div>
                    <div>
                      <Label>Severity</Label>
                      <p className={`mt-1 text-sm font-semibold ${cfg.text}`}>{cfg.label}</p>
                    </div>
                    <div>
                      <Label>Timestamp</Label>
                      <div className="mt-1 flex items-center gap-1">
                        <Clock size={13} className="text-slate-400" />
                        <p className="text-sm font-semibold text-slate-900">
                          {a.time} - {a.date}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Image Preview in Expanded View */}
                  {a.hasImages && (
                    <div>
                      <Label>Captured Image</Label>
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewAlert({
                              imgUrls: a.imgUrls,
                              cam: a.cam,
                              location: a.location,
                              module: a.module,
                              currentIndex: 0,
                            });
                          }}
                          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100"
                        >
                          <Eye size={14} />
                          View Full Size Image
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {!loading && alerts.length === 0 && (
          <div className="py-16 text-center bg-white rounded-xl border border-slate-200">
            <CheckCircle2 size={48} className="mx-auto mb-3 text-slate-400" />
            <p className="font-semibold text-slate-700">No alerts match the current filters</p>
            <p className="text-sm text-slate-500 mt-1">Try adjusting your filter criteria</p>
          </div>
        )}
      </div>

      {/* ── ANALYTICS SECTION ── */}
      {/* {!loading && !analyticsLoading && alerts.length > 0 && analytics.byType.length > 0 && ( */}
         {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4"> */}
          {/* Events by type */}
          {/* <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Events by type</h3>
                <p className="text-[10px] text-slate-500">Last 30 days · {analytics.totalAlerts} alerts</p>
              </div>
              <BarChart3 size={16} className="text-slate-400" />
            </div>
            <div className="p-4 space-y-2">
              {analytics.byType.slice(0, 7).map((item) => (
                <div key={item.type} className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900">{item.percentage}%</span>
                    <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full bg-violet-500" 
                        style={{ width: `${Math.min(item.percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div> */}

          {/* Outcome breakdown */}
          {/* <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Outcome breakdown</h3>
                <p className="text-[10px] text-slate-500">Last 30 days · {analytics.totalAlerts} alerts</p>
              </div>
              <PieChart size={16} className="text-slate-400" />
            </div>
            <div className="p-4 space-y-3">
              {analytics.outcomeBreakdown.map((item) => (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-700">{item.label}</span>
                    <span className="text-xs font-bold text-slate-900">{item.percentage}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full" 
                      style={{ width: `${item.percentage}%`, background: item.color }}
                    />
                  </div>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-start gap-2 text-[10px] text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <AlertCircle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>
                    {analytics.insights?.criticalCount > 0 
                      ? `${analytics.insights.criticalCount} critical alerts (${analytics.insights.criticalPercentage}%) this month`
                      : 'No critical alerts this month'}
                  </span>
                </div>
              </div>
            </div>
          </div> */}

          {/* Repeat locations */}
          {/* <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Repeat locations</h3>
                <p className="text-[10px] text-slate-500">Alerts per camera, 30 days</p>
              </div>
              <MapPin size={16} className="text-slate-400" />
            </div>
            <div className="p-4 space-y-2">
              {analytics.repeatLocations.slice(0, 5).map((item, index) => (
                <div key={item.cam} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 w-4">{index + 1}</span>
                    <Camera size={12} className="text-slate-400" />
                    <span className="text-xs font-medium text-slate-700">{item.cam}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-900">{item.count}</span>
                </div>
              ))}
              {analytics.repeatLocations.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-start gap-2 text-[10px] text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                    <AlertTriangle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>
                      {analytics.insights?.topLocations} account for {analytics.insights?.topLocationsPercentage}% of all alerts
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div> */}
        {/* </div> */}
      {/* )} */}

      {/* ── Image Preview Modal ── */}
      <Modal
        open={!!previewAlert}
        onCancel={() => {
          setPreviewAlert(null);
          setImageLoading(false);
        }}
        footer={null}
        centered
        title={
          previewAlert && (
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className="text-violet-600" />
              <span className="text-sm font-semibold">
                {previewAlert.cam} - {previewAlert.location}
              </span>
              <span className="text-xs text-slate-500 ml-2">{previewAlert.module}</span>
            </div>
          )
        }
        width={800}
        styles={{ body: { padding: "20px" } }}
      >
        {previewAlert && (() => {
          const current = previewAlert.imgUrls[previewAlert.currentIndex];
          return (
            <div className="flex flex-col gap-4">
              <div className="relative min-h-[400px] bg-slate-100 rounded-lg overflow-hidden">
                {imageLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-violet-500" />
                      <span className="text-sm text-slate-500">Loading image...</span>
                    </div>
                  </div>
                )}
                <img
                  key={current.url}
                  src={current.url}
                  alt={`Security snapshot from ${previewAlert.cam}`}
                  className="w-full h-auto rounded-lg"
                  style={{ display: imageLoading ? "none" : "block" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="hidden items-center justify-center h-96 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400">
                  <div className="text-center">
                    <ImageOff size={32} className="mx-auto mb-2 text-slate-300" />
                    <p>Image failed to load</p>
                    <p className="text-xs mt-1">URL: {current.url?.substring(0, 50)}...</p>
                  </div>
                </div>
              </div>

              {current.time && (
                <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                  <span>Captured: {isoToTime(current.time)}</span>
                  {current.count && <span>Persons: {current.count}</span>}
                </div>
              )}

              {previewAlert.imgUrls.length > 1 && (
                <div className="flex items-center justify-between gap-3 pt-2">
                  <button
                    disabled={previewAlert.currentIndex === 0}
                    onClick={() =>
                      setPreviewAlert((p) => ({ ...p, currentIndex: p.currentIndex - 1 }))
                    }
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs font-medium text-slate-500">
                    Frame {previewAlert.currentIndex + 1} of {previewAlert.imgUrls.length}
                  </span>
                  <button
                    disabled={previewAlert.currentIndex === previewAlert.imgUrls.length - 1}
                    onClick={() =>
                      setPreviewAlert((p) => ({ ...p, currentIndex: p.currentIndex + 1 }))
                    }
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
      
      {!loading && totalAlerts > 0 && (
        <div className="flex justify-end mt-6">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalAlerts}
            showSizeChanger
            pageSizeOptions={["25", "50", "75", "100"]}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            onShowSizeChange={(page, size) => {
              setCurrentPage(1);
              setPageSize(size);
            }}
            showTotal={(total) => `Total ${total} alerts`}
          />
        </div>
      )}
    </div>
  );
};

export default Alerts;